import {
  Conversation,
  ConversationMessage,
  DocumentInfo,
} from "@mirage/service-conversation/types";
import {
  isAskWithMultiDocsConversationState,
  isAskWithPromptMultiDocsConversationState,
  isSummarizeItemConversationState,
} from "@mirage/service-conversation/utils/buildConversationRouterState";
import { convertChatSourceToDocumentInfo } from "@mirage/service-conversation/utils/convertChatSourceToDocumentInfo";
import { findLast } from "lodash";
import { useMemo } from "react";

export type StackChatMetadata = {
  hasUserSentFirstMessage: boolean;
  lastUserMessage: string;
  sharingId: string;
  sourceDocumentIds: string[];
  stackSources: DocumentInfo[];
};

export function useStackChatDetails(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  routerState: any,
  conversation?: Conversation,
): StackChatMetadata | null {
  const stackChatMetadata = useMemo(() => {
    // Attempt to parse Stack Chat metadata from both the conversation and the router state.
    return (
      parseStackChatConversation(conversation) ||
      parseStackChatRouterState(routerState)
    );
  }, [routerState, conversation?.clientContext]);

  const messageInfo = useMemo(() => {
    // Check whether a non-empty message has been sent on behalf of the user
    const hasUserSentFirstMessage = !!conversation?.messages?.some(
      (message) => {
        return (
          message.messageType?.case === "userMessage" &&
          message.messageType.value.text !== ""
        );
      },
    );

    return {
      lastUserMessage: getLastUserMessage(conversation?.messages || []),
      hasUserSentFirstMessage,
    };
  }, [conversation?.messages]);

  // If the conversation is not a Stack Chat, return null.
  if (!stackChatMetadata) {
    return null;
  }

  return {
    ...stackChatMetadata,
    ...messageInfo,
  };
}

export function getLastUserMessage(messages: ConversationMessage[]) {
  // Returns the last message sent on behalf of the user in a Stack Chat conversation. This value
  // is used to pre-fill the message input when starting a new conversation based on newly available
  // sources.
  return (
    findLast(
      messages,
      (message) =>
        message.messageType?.case === "userMessage" &&
        !message.messageType?.value.llmIgnore,
    )?.messageType?.value?.text || ""
  );
}

export function getPersistedStackChatSources(messages: ConversationMessage[]) {
  // For Stack Chats (those with a clientScenario of type "stack"), we can, at least for now,
  // safely assume that the first user message is the one that contains the attachments used to
  // create the conversation. This helper finds that message and returns the list of uuids for
  // the attachments so that they can be mapped against the Stack metadata in the sidebar.
  for (const message of messages) {
    if (
      message.messageType?.case === "userMessage" &&
      message.messageType.value.attachments
    ) {
      return message.messageType.value.attachments;
    }
  }

  return [];
}

export function parseStackChatConversation(conversation?: Conversation) {
  // Attempt to parse the Stack sharing id and chat sources from an existing Conversation.
  // If the conversation does not exist, is missing a clientContext object, or has is not
  // a Stack Chat, returns null.
  if (conversation?.clientContext?.clientScenario === "stack") {
    const stackSources = getPersistedStackChatSources(conversation.messages);
    const sourceDocumentIds = stackSources.map((source) => source.uuid);

    return {
      sharingId: conversation.clientContext.clientScenarioId,
      sourceDocumentIds,
      stackSources,
    };
  }

  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseStackChatRouterState(routerState: any) {
  // Attempt to parse the Stack sharing id and chat sources from the router state. If the
  // router state does not exist, does not represent a valid Stack Chat conversation state, or
  // is for a non-Stack scenario, returns null.
  if (
    routerState &&
    (isAskWithPromptMultiDocsConversationState(routerState) ||
      isAskWithMultiDocsConversationState(routerState) ||
      isSummarizeItemConversationState(routerState)) &&
    routerState.scenarioMeta?.type === "stack"
  ) {
    const stackSources = routerState.chatSources.map(
      convertChatSourceToDocumentInfo,
    );
    const sourceDocumentIds = stackSources.map((source) => source.uuid);

    return {
      sharingId: routerState.scenarioMeta.sharingId,
      sourceDocumentIds,
      stackSources,
    };
  }

  return null;
}
